import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { adminAPI } from '../../api/client'
import type { AdminSearchResults, AdminSection } from './adminTypes'

type Props = {
  token: string
  open: boolean
  onClose: () => void
  onNavigate: (section: AdminSection, entity?: { type: 'user' | 'listing' | 'imei'; id: number }) => void
}

export default function AdminGlobalSearch({ token, open, onClose, onNavigate }: Props) {
  const { t } = useTranslation()
  const [q, setQ] = useState('')
  const [results, setResults] = useState<AdminSearchResults | null>(null)
  const [loading, setLoading] = useState(false)

  const search = useCallback(() => {
    const needle = q.trim()
    if (needle.length < 2) {
      setResults(null)
      return
    }
    setLoading(true)
    adminAPI
      .search(token, { q: needle })
      .then((res) => setResults(res.data as AdminSearchResults))
      .finally(() => setLoading(false))
  }, [token, q])

  useEffect(() => {
    if (!open) return
    const tmr = window.setTimeout(search, 200)
    return () => window.clearTimeout(tmr)
  }, [open, search])

  useEffect(() => {
    if (!open) {
      setQ('')
      setResults(null)
    }
  }, [open])

  if (!open) return null

  return (
    <div className="vc-admin-search-modal" role="dialog" aria-modal="true">
      <button type="button" className="vc-admin-drawer__backdrop" onClick={onClose} aria-label="Close" />
      <div className="vc-admin-search-modal__panel">
        <input
          autoFocus
          type="search"
          className="vc-admin-input vc-admin-input--search"
          placeholder={t('admin.globalSearchPlaceholder')}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        {loading ? <p className="meta-text">{t('common.loading')}</p> : null}
        {results ? (
          <div className="vc-admin-search-results">
            {results.users.length > 0 ? (
              <section>
                <h4>{t('admin.nav.users')}</h4>
                <ul>
                  {results.users.map((user) => (
                    <li key={user.id}>
                      <button
                        type="button"
                        onClick={() => {
                          onNavigate('users', { type: 'user', id: user.id })
                          onClose()
                        }}
                      >
                        {user.full_name || user.email}
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
            {results.listings.length > 0 ? (
              <section>
                <h4>{t('admin.nav.listings')}</h4>
                <ul>
                  {results.listings.map((listing) => (
                    <li key={listing.id}>
                      <button
                        type="button"
                        onClick={() => {
                          onNavigate('listings', { type: 'listing', id: listing.id })
                          onClose()
                        }}
                      >
                        {listing.manufacturer} {listing.model_name} · €{listing.price}
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
            {results.imei.length > 0 ? (
              <section>
                <h4>IMEI</h4>
                <ul>
                  {results.imei.map((row) => (
                    <li key={row.id}>
                      <button
                        type="button"
                        onClick={() => {
                          onNavigate('imei', { type: 'imei', id: row.id })
                          onClose()
                        }}
                      >
                        {row.imei_masked} · {row.user_email}
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
            {!loading &&
            results.users.length === 0 &&
            results.listings.length === 0 &&
            results.imei.length === 0 ? (
              <p className="vc-admin-muted">{t('admin.searchNoResults')}</p>
            ) : null}
          </div>
        ) : null}
        <p className="vc-admin-muted vc-admin-search-modal__hint">{t('admin.globalSearchHint')}</p>
      </div>
    </div>
  )
}
